import React from 'react';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';
import { UtilityFunctions } from '../../utils/UtilityFunctions';
import { cookieService, userService } from '../../_services';
import { STRING_CONSTANTS } from '../../utils/constants/stringConstants';
import { api } from '../../middleware/api';
import { checkData } from '../../utils/checkData';

const sidebarStyles: { [key: string]: React.CSSProperties } = {
  container: { backgroundColor: '#F8F9FA', minHeight: '100vh' },
  topBar: {
    background: '#FFFFFF',
    padding: '16px 30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    borderBottom: '1px solid #E0E0E0',
  },
  topBarTitle: { color: '#141414', margin: 0, fontSize: '24px', fontWeight: 700 as any },
};

/* Clean inline SVG icons — no emoji */
const DashboardIcon = ({ active }: { active: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? '#0065FF' : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const JobsIcon = ({ active }: { active: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? '#0065FF' : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
  </svg>
);

const CandidatesIcon = ({ active }: { active: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? '#0065FF' : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87" />
    <path d="M16 3.13a4 4 0 010 7.75" />
  </svg>
);

const ResultsIcon = ({ active }: { active: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? '#0065FF' : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 20V10" />
    <path d="M12 20V4" />
    <path d="M6 20v-6" />
  </svg>
);

const ProfileIcon = ({ active }: { active: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? '#0065FF' : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const QuestionsIcon = ({ active }: { active: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? '#0065FF' : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 11h6" />
    <path d="M9 15h4" />
    <path d="M5 3h14a2 2 0 012 2v14l-4-4H5a2 2 0 01-2-2V5a2 2 0 012-2z" />
  </svg>
);

const InvitesIcon = ({ active }: { active: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? '#0065FF' : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16v16H4z" />
    <path d="M22 6l-10 7L2 6" />
  </svg>
);

const BillingIcon = ({ active }: { active: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? '#0065FF' : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const LogoutIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const navItems = [
  { icon: DashboardIcon, path: '/interview-simulator/dashboard', title: 'Dashboard' },
  { icon: ProfileIcon, path: '/interview-simulator/profile', title: 'Profile' },
  { icon: QuestionsIcon, path: '/interview-simulator/questions', title: 'Questions' },
  { icon: InvitesIcon, path: '/interview-simulator/invites', title: 'Invites' },
  { icon: JobsIcon, path: '/interview-simulator/jobs', title: 'Jobs' },
  { icon: CandidatesIcon, path: '/interview-simulator/candidates', title: 'Candidates' },
  { icon: ResultsIcon, path: '/interview-simulator/results', title: 'Results' },
  { icon: BillingIcon, path: '/interview-simulator/billing', title: 'Billing' },
];

interface OwnProps {
  children: React.ReactNode;
  title?: string;
}

type Props = OwnProps & RouteComponentProps;

const InterviewSidebarInner: React.FC<Props> = ({ children, title, history, location }) => {
  const currentPath = location.pathname;

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    userService
      .logout()
      .then(() => {
        api
          .deleteApi('device-token/', {
            deviceType: 'WEB',
            token: `${cookieService.getCookie('fcm')}`,
          })
          .then(() => {
            cookieService.removeAllCookies();
            history.push(STRING_CONSTANTS.navigationString.login);
          })
          .catch(() => {
            cookieService.removeAllCookies();
            history.push(STRING_CONSTANTS.navigationString.login);
          });
      })
      .catch((err: any) => {
        checkData({ history } as any, '/', err);
      });
  };

  const pageTitle = title || (() => {
    if (currentPath.includes('/profile')) return 'Your Profile';
    if (currentPath.includes('/questions')) return 'Question Sets';
    if (currentPath.includes('/invites')) return 'Branded Invites';
    if (currentPath.includes('/jobs/') && currentPath.includes('/start')) return 'Start Interview';
    if (currentPath.includes('/jobs/')) return 'Job Detail';
    if (currentPath.includes('/jobs')) return 'Jobs';
    if (currentPath.includes('/candidates')) return 'Candidates';
    if (currentPath.includes('/results/')) return 'Result Detail';
    if (currentPath.includes('/results')) return 'Results';
    if (currentPath.includes('/billing')) return 'Billing';
    return 'Interview Simulator';
  })();

  return (
    <section className="main-dashboard-wrap" style={sidebarStyles.container}>
      <div id="wrapper" style={{ height: '100vh' }}>
        {/* Sidebar */}
        <div style={{
          width: '90px',
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 0',
          borderRight: '1px solid #E0E0E0',
        }}>
          <div>
            <Link to="/interview-simulator/dashboard" title="Dashboard">
              <img
                src={UtilityFunctions.getImageURL() + 'db-logo.svg'}
                style={{ width: '48px', display: 'block', margin: '0 auto' }}
                alt="MPLOI"
              />
            </Link>

            <div style={{ marginTop: '28px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              {navItems.map(item => {
                const isActive = currentPath === item.path || currentPath.startsWith(item.path + '/');
                const IconComponent = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    title={item.title}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '64px',
                      height: '54px',
                      borderRadius: '10px',
                      textDecoration: 'none',
                      background: isActive ? '#F0F4FF' : 'transparent',
                      border: isActive ? '1px solid #0065FF' : '1px solid transparent',
                      transition: 'all 0.15s',
                      gap: '3px',
                    }}
                  >
                    <IconComponent active={isActive} />
                    <span style={{
                      fontSize: '10px',
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? '#0065FF' : '#6B7280',
                      lineHeight: 1,
                    }}>
                      {item.title}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Bottom: Logout */}
          <div
            style={{
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '3px',
            }}
            onClick={handleLogout}
            title="Logout"
          >
            <LogoutIcon />
            <span style={{ fontSize: '10px', color: '#6B7280' }}>Logout</span>
          </div>
        </div>

        {/* Content Wrapper */}
        <div id="content-wrapper" className="d-flex flex-column">
          <div id="content">
            <div style={sidebarStyles.topBar}>
              <h1 style={sidebarStyles.topBarTitle}>{pageTitle}</h1>
            </div>
            <div className="container-fluid">
              {children}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const InterviewSidebar = withRouter(InterviewSidebarInner);
export default InterviewSidebar;
